import { z } from "zod";

/** Créneau récurrent (jour + horaire) dédié à une prestation collective. */
export const ServiceScheduleSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: z.string().regex(/^([0-1]\d|2[0-3]):[0-5]\d$/, "Horaire invalide (HH:MM)"),
  endTime: z.string().regex(/^([0-1]\d|2[0-3]):[0-5]\d$/, "Horaire invalide (HH:MM)"),
  isActive: z.boolean().optional().default(true),
});

export type ServiceScheduleDto = z.infer<typeof ServiceScheduleSchema>;

export const ServiceItemSchema = z.object({
  label: z.string().min(1),
  duration: z.number().int().min(15).max(480),
  price: z.number().min(0),
  maxBookingsPerClient: z.number().int().min(1).nullable().optional(),
  /**
   * Nombre de pratiquants acceptés simultanément sur un même créneau.
   * 1 (défaut) = prestation individuelle (ex. Personal Training) ; >1 = séance
   * collective (ex. cours collectif), avec liste d'attente automatique au-delà.
   */
  maxParticipants: z.number().int().min(1).max(500).optional().default(1),
  /**
   * Créneaux jour/horaire dédiés à la prestation. Liste vide = la prestation reste
   * réservable sur les disponibilités générales du praticien (comportement historique).
   */
  schedules: z.array(ServiceScheduleSchema).optional().default([]),
  description: z.unknown().nullable().optional(),
  /**
   * Image illustrant la prestation.
   * À l'écriture : data URL (`data:image/...;base64,...`) ou URL déjà servie par l'API.
   * À la lecture : URL de média chiffrée (`/organizations/media/...`).
   */
  image: z.string().nullable().optional(),
  /** Cadrage vertical de la vignette image (`object-top` / `object-center` / `object-bottom`). */
  imageObjectPosition: z
    .enum(["TOP", "CENTER", "BOTTOM"])
    .optional()
    .default("CENTER"),
  /**
   * Couleur hexadécimale (#RRGGBB) du motif, utilisée pour colorer les rendez-vous
   * dans l'agenda. Null/undefined = couleur déduite du statut du RDV.
   */
  color: z
    .string()
    .regex(/^#([0-9a-fA-F]{6})$/, "Couleur hexadécimale invalide (#RRGGBB)")
    .nullable()
    .optional(),
  isActive: z.boolean().default(true),
  /** Affiche la prestation sur la page de marque publique. */
  publicListed: z.boolean().optional().default(true),
  position: z.number().int().default(0),
  /** Groupe d'agencement sur la vitrine (null = "sans groupe"). */
  groupId: z.string().nullable().optional(),
  registrationMinLeadMinutes: z.number().int().min(0).optional().default(0),
  registrationMaxAdvanceMinutes: z
    .number()
    .int()
    .min(-1)
    .optional()
    .default(-1), // -1 means unlimited
  locationMode: z
    .enum(["ONSITE", "REMOTE"])
    .optional()
    .default("ONSITE"),
  /** Consignes pour le client avant la séance (affichées sur le portail). */
  clientPreparationNotes: z.string().max(8000).nullable().optional(),
});

export type ServiceItemDto = z.infer<typeof ServiceItemSchema>;
